import React, { useState, useEffect, useRef } from "react";
import { useT, LOCALES } from "../i18n/LanguageContext";
import { ChevronDown, Globe, Check } from "lucide-react";

export default function LanguageSwitcher({ variant = "default" }) {
  const { locale, setLocale } = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = LOCALES.find((l) => l.code === locale) || LOCALES[0];

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          variant === "mobile"
            ? "inline-flex items-center gap-2 border border-white/25 bg-white/5 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white"
            : "inline-flex items-center gap-2 border border-white/25 bg-white/[0.04] px-3.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-red hover:bg-brand-red/10"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        data-testid="language-switcher-trigger"
      >
        <Globe className="h-3.5 w-3.5 text-brand-red" strokeWidth={1.8} />
        <span className="text-white">{current.short}</span>
        <span className="hidden text-brand-textDim sm:inline">/ {current.label}</span>
        <ChevronDown
          className={`h-3 w-3 text-brand-textDim transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.8}
        />
      </button>

      {open && (
        <ul
          className="absolute end-0 z-50 mt-2 w-56 border border-white/15 bg-[#0A0A0A] py-1 shadow-2xl"
          role="listbox"
          data-testid="language-switcher-menu"
        >
          <li className="border-b border-white/10 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.25em] text-brand-textDim">
            Language · Langue
          </li>
          {LOCALES.map((l) => {
            const active = l.code === locale;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLocale(l.code);
                    setOpen(false);
                  }}
                  data-testid={`language-option-${l.code}`}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/5 ${
                    active ? "text-brand-red" : "text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    {active ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2} />
                    ) : (
                      <span className="inline-block h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    <span>{l.label}</span>
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-brand-textDim">
                    {l.short}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
